import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { getRequiredFields, removeEmptyFields } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import {
  COMPANY_DESC,
  COMPANY_NAME,
  EXP_REQ,
  JOB_DESC,
  JOB_ROLE,
  JOB_TITLE,
  LOCATION,
  REQ_SKILLS,
  STATUS,
} from "./consts";
import { routes } from "@/routes/utils";
import { toast } from "sonner";
import {
  useAddJobPostingMutation,
  useGetJobPostingById,
  useUpdateJobPostingMutation,
} from "@/services";
import { useIsMobile } from "@/hooks/use-mobile";
import { useEffect, useState } from "react";
import { formValues, schema } from "./validations";
import { useLocation } from "react-router-dom";

const useAddEdit = () => {
  const { action, id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isView = action == "view";
  const isEdit = action == "edit";
  const isMobile = useIsMobile();
  const [keys, setKeys] = useState({ company: Math.random() * 2 });

  const location = useLocation();
  const applicableCompany = location.state?.applicableCompany;

  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      [JOB_TITLE]: "",
      [JOB_ROLE]: "",
      [LOCATION]: "",
      [JOB_DESC]: "",
      [REQ_SKILLS]: "",
      [EXP_REQ]: "",
      [COMPANY_NAME]: "",
      [COMPANY_DESC]: "",
      [STATUS]: "ACTIVE",
    },
  });

  const requiredFields = getRequiredFields(schema);

  const onBackClick = () => {
    navigate(`/${routes.admin}/${routes.jobPosting}`);
  };

  const { data: jobPostingData, isLoading: isJobPostingDataLoading } =
    useGetJobPostingById({
      id: id as string,
      queryParams: {
        enabled: !!id,
      },
    });

  useEffect(() => {
    if (jobPostingData) {
      form.reset({
        [JOB_TITLE]: jobPostingData?.data?.[JOB_TITLE]?.toString() || "",
        [JOB_ROLE]: jobPostingData?.data?.[JOB_ROLE]?.toString() || "",
        [LOCATION]: jobPostingData?.data?.[LOCATION]?.join(";") || "",
        [JOB_DESC]: jobPostingData?.data?.[JOB_DESC]?.toString() || "",
        [REQ_SKILLS]: jobPostingData?.data?.[REQ_SKILLS]?.join(";") || "",
        [EXP_REQ]: jobPostingData?.data?.[EXP_REQ]?.join(";") || "",
        [COMPANY_NAME]:
          jobPostingData?.data?.[COMPANY_NAME]?.toString()?.trim() || "",
        [COMPANY_DESC]: jobPostingData?.data?.[COMPANY_DESC]?.toString() || "",
        [STATUS]: "ACTIVE",
      });
      setKeys({ company: Math.random() * 3 });
    }
  }, [jobPostingData]);

  const { mutate: addInfo, isPending: isAddPending } = useAddJobPostingMutation(
    {
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.issues?.error === "string"
            ? error?.response?.data?.issues?.error
            : typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong",
          {
            className: "!bg-red-500 !text-white",
          }
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Job Posting Added Successfully", {
          className: "!bg-green-600 !text-white",
        });
        queryClient.invalidateQueries({
          queryKey: ["job-posting"],
        });
        onBackClick();
      },
    }
  );

  const { mutate: updateInfo, isPending: isUpdatePending } =
    useUpdateJobPostingMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.issues?.error === "string"
            ? error?.response?.data?.issues?.error
            : typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong",
          {
            className: "!bg-red-500 !text-white",
          }
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Job Posting Updated Successfully", {
          className: "!bg-green-600 !text-white",
        });
        queryClient.invalidateQueries({
          queryKey: ["job-posting"],
        });
        onBackClick();
      },
    });

  const onSubmit = (data: formValues) => {
    let _data = removeEmptyFields(data);

    const splitBySemicolon = (value?: string) =>
      value?.split(";").map(v => v.trim()).filter(Boolean) || [];

    if (id) {
      updateInfo({
        id,
        data: {
          ..._data,
          [LOCATION]: splitBySemicolon(_data[LOCATION] as string),
          [REQ_SKILLS]: splitBySemicolon(_data[REQ_SKILLS] as string),
          [EXP_REQ]: splitBySemicolon(_data[EXP_REQ] as string),
        },
      });
    } else {
      addInfo({
        data: {
          ..._data,
          [LOCATION]: splitBySemicolon(_data[LOCATION] as string),
          [REQ_SKILLS]: splitBySemicolon(_data[REQ_SKILLS] as string),
          [EXP_REQ]: splitBySemicolon(_data[EXP_REQ] as string),
          applicableCompany:
            applicableCompany === "MBAPL"
              ? "MBAPL"
              : applicableCompany === "KPL"
              ? "KPL"
              : "OSTWAL",
        },
      });
    }
  };

  return {
    form,
    onSubmit,
    requiredFields,
    isView,
    onCancel: onBackClick,
    isAddPending,
    isMobile,
    isJobPostingDataLoading,
    action,
    isUpdatePending,
    keys,
    isEdit,
  };
};

export default useAddEdit;
